import React, { useEffect, useState } from 'react';

import { useAppDispatch, useAppSelector } from '~redux/hooks';
import combinedActions from '~redux/combinedActions';

import TabPageHeaderWrapper from '~components/atoms/TabPageHeaderWrapper';
import Text from '~components/atoms/Text';
import ActionButton from '~components/atoms/ActionButton';
import TabPageContainer from '~components/atoms/TabPageContainer';

import AdminsSearchAndFilterBar from './AdminsSearchAndFilterBar';
import AdminsTable from './AdminsTable';
import AdminsPaginator from './AdminsPaginator';
import AdminsCreateAdminModal from './AdminsCreateAdminModal';
import AdminsAdminDetailsModal from './AdminsAdminDetailsModal';
import AdminsDeleteAdminModal from './AdminsDeleteAdminModal';

import { ExportPermissionsEnum, ModuleEnum, PermissionsEnum } from '~utils/enums';
import * as Styles from './_admins.module.scss';
import UtilFunctions from '~utils/functions';
import DownloadCSV from '~components/molecules/DownloadCSV';
import { TExportAdminUserPermission } from '~types/feat/admin';
import dayjs from 'dayjs';

const Admins = () => {
    const dispatch = useAppDispatch();

    const { Misc: { dateTimeFormat } } = UtilFunctions;

    const handleCreateNewAdminClick = () => dispatch(combinedActions.adminSetCreateNewAdminModalIsOpen(true));
    const exportAdminUserListing = () => dispatch(combinedActions.adminExportAdminUserListingAttempt());
    const clearAdminUserListingForExport = () => dispatch(combinedActions.adminClearAdminUserListingForExport());

    const [isExportingAdminUserListing, setIsExportingAdminUserListing] = useState(false);
    const modules = Object.values(ModuleEnum).filter(module => Number.isNaN(Number(module))) as string[];
    const permissionValues = Object.values(ExportPermissionsEnum).filter(module => Number.isNaN(Number(module))) as string[];

    const getAdminUserListingForExportIsLoading = useAppSelector(state => state.admin.loadings.exportAdminUserListing);
    const createAccessModules = useAppSelector(state => state.auth.userDetails.access)?.find(access => access.permission === PermissionsEnum.Create)?.modules || [];
    const adminUserListingForExport = useAppSelector(state => state.admin.adminUserListingForExport);

    useEffect(() => {
        dispatch(combinedActions.branchGetAllBranchesAttempt());
    }, []);

    const handleExportInventoryTransactionListingCleanup = () => {
        clearAdminUserListingForExport();
        setIsExportingAdminUserListing(false);
    };

    const listHeader = [
        'USER ID',
        'STATUS',
        'SAM ACCOUNT NAME',
        'DISPLAY NAME',
        'BRANCH ID',
        'ROLE ID',
        'EMAIL',
        'REFERRAL CODE',
        'CREATED AT',
        'UPDATED AT',
        'ROLE NAME',
        'DASHBOARD VIEW',
        'DASHBOARD CREATE',
        'DASHBOARD EDIT',
        'CUSTOMER MANAGEMENT VIEW',
        'CUSTOMER MANAGEMENT CREATE',
        'CUSTOMER MANAGEMENT EDIT',
        'PRODUCT VIEW',
        'PRODUCT CREATE',
        'PRODUCT EDIT',
        'BUY & SELL GOLD VIEW',
        'BUY & SELL GOLD CREATE',
        'BUY & SELL GOLD EDIT',
        'CONVERSION VIEW',
        'CONVERSION CREATE',
        'CONVERSION EDIT',
        'APPROVAL VIEW',
        'APPROVAL CREATE',
        'APPROVAL EDIT',
        'BRANCH MANAGEMENT VIEW',
        'BRANCH MANAGEMENT CREATE',
        'BRANCH MANAGEMENT EDIT',
        'RBAC VIEW',
        'RBAC CREATE',
        'RBAC EDIT',
        'INVENTORY VIEW',
        'INVENTORY CREATE',
        'INVENTORY EDIT',
        'INVENTORY TRANSACTION VIEW',
        'INVENTORY TRANSACTION CREATE',
        'INVENTORY TRANSACTION EDIT',
        'DISCOUNT VIEW',
        'DISCOUNT CREATE',
        'DISCOUNT EDIT',
        'TRANSFER VIEW',
        'TRANSFER CREATE',
        'TRANSFER EDIT',
        'SETTING VIEW',
        'SETTING CREATE',
        'SETTING EDIT',
        'REPORT VIEW',
        'REPORT CREATE',
        'REPORT EDIT',
        'PROJECT VIEW',
        'PROJECT CREATE',
        'PROJECT EDIT',
    ];

    const listData = adminUserListingForExport.map(user => {
        const {
            userId,
            status,
            sAMAccountName,
            displayName,
            branchId,
            roleId,
            email,
            referralCode,
            createdAt,
            updatedAt,
            roleName,
            permission,
        } = user;

        const renderPermissionEnum = (permissions: TExportAdminUserPermission[]) => {
            return modules.map((module) => {
                const filteredModule = permissions.filter(item => item.moduleName === (module === 'Buy & Sell Gold' ? 'BuySellGold' : module.replace(/ /g, '')));

                const cleanedList = filteredModule.filter((obj, index, self) =>
                    index === self.findIndex((o) => o.moduleName === obj.moduleName && o.permission === obj.permission)
                ).sort((a, b) => a.permission - b.permission);

                return permissionValues.map((value) => {
                    const filteredPermission = cleanedList.find(item => ExportPermissionsEnum[item.permission] === value);

                    return filteredPermission ? 'X' : 'B';
                });
            });

        };

        return {
            userId: userId || '-',
            status: status || '-',
            sAMAccountName: sAMAccountName || '-',
            displayName: displayName || '-',
            branchId: branchId || '-',
            roleId: roleId || '-',
            email: email || '-',
            referralCode: referralCode || '-',
            createdAt: dateTimeFormat(createdAt) || '-',
            updatedAt: dateTimeFormat(updatedAt) || '-',
            roleName: roleName || '-',
            permission: renderPermissionEnum(permission) || '-',
        };
    });

    return (
        <TabPageContainer>
            <TabPageHeaderWrapper>
                <Text
                    label='Admin List'
                    className={Styles.title}
                    size='l'
                    isBold
                />

                <div className={Styles['btn-container']}>
                    {createAccessModules.find(module => module === ModuleEnum.RBAC)
                        && (
                            <DownloadCSV
                                buttonLabel='Export Admins List to CSV'
                                header={listHeader}
                                data={listData}
                                fileName={dayjs().format('DD-MM-YYYY').concat(' Admin User Listing')}
                                onClick={exportAdminUserListing}
                                cleanup={handleExportInventoryTransactionListingCleanup}
                                isLoading={getAdminUserListingForExportIsLoading || isExportingAdminUserListing}
                            />
                        )
                    }

                    {createAccessModules.find(module => module === ModuleEnum.RBAC)
                        && (
                            <ActionButton
                                label='+ Create New User'
                                onClick={handleCreateNewAdminClick}
                            />
                        )
                    }
                </div>

            </TabPageHeaderWrapper>

            <AdminsSearchAndFilterBar />

            <AdminsTable />

            <AdminsPaginator />

            <>
                <AdminsCreateAdminModal />

                <AdminsAdminDetailsModal />

                <AdminsDeleteAdminModal />
            </>
        </TabPageContainer>
    );
};

export default Admins;
